import { useQuery } from '@tanstack/react-query'
import { ApiError, useApiFetch } from '../../shared/api'
import type { HuntReplayChatDto } from './huntReplayChatDto'
import { HUNT_REPLAY_QUERY_KEY } from './useHuntReplay'

/**
 * A hunt's chat, for the replay.
 *
 * A second request beside the archive, not part of it, and the difference in
 * how the two are cached is the point rather than an oversight. An ended hunt's
 * archive never changes, so `useHuntReplay` holds it forever. Chat does change:
 * a message deleted after the hunt must stop being shown, and the endpoint
 * answers `no-store` for exactly that reason. Keeping it here would serve a
 * recalled message from memory for the rest of the session.
 *
 * Nothing here is allowed to break the replay. The caller renders the hunt
 * whether or not this resolves — see `ReplayRoute`.
 */
export function useHuntReplayChat(huntId: string | undefined, enabled: boolean) {
  const apiFetch = useApiFetch()

  return useQuery<HuntReplayChatDto, ApiError>({
    queryKey: [HUNT_REPLAY_QUERY_KEY, 'hunt-replay-chat', huntId],
    enabled: enabled && huntId !== undefined && huntId !== '',
    queryFn: () => apiFetch<HuntReplayChatDto>(`/hunts/${encodeURIComponent(huntId!)}/replay/chat`),
    // A minute, not Infinity: the payload is small and a deletion should reach a
    // viewer who reopens the replay rather than waiting for a new session.
    staleTime: 60_000,
    refetchOnWindowFocus: false,
    // 403 means the viewer was not on this hunt — the answer for a share-link
    // viewer, and a permanent one. 404 and 502 are not fixed by asking again
    // either. Anything else gets one retry.
    retry: (failureCount, error) =>
      error.status !== 403 && error.status !== 404 && error.status !== 502 && failureCount < 1,
  })
}
